import { bexHttpClient } from '../../clients';

const BEX_BASE = process.env.REACT_APP_BEX_SERVER_URL || '';

export type BulkProvisionLog = {
    type: 'log' | 'progress' | 'done';
    message?: string;
    index?: number;
    total?: number;
    instanceId?: string;
    status?: string;
    data?: unknown[];
};

class AssistantProvisioningService {

    async bulkProvisionAssistant(data: {
        count: number;
        template_id: string;
        template_org_id: string;
        template_sub_org_id: string;
        target_org_id: string;
        target_sub_org_id: string;
    }) {
        const response = await bexHttpClient.put('/assistant/provisioning/provision/bulk', data);
        return response;
    }

    /**
     * Bulk provision with streaming NDJSON progress logs.
     * Calls onLog for each streamed line.
     */
    async bulkProvisionAssistantStream(
        data: {
            count: number;
            template_id: string;
            template_org_id: string;
            template_sub_org_id: string;
            target_org_id: string;
            target_sub_org_id: string;
            email?: string;
        },
        onLog: (log: BulkProvisionLog) => void
    ): Promise<void> {
        // Get auth token from axios defaults
        const token = (bexHttpClient.defaults.headers.common?.['Authorization'] as string)
            || (bexHttpClient.defaults.headers?.['Authorization'] as string)
            || '';

        const res = await fetch(`${BEX_BASE}/api/assistant/provisioning/provision/bulk`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': token } : {}),
            },
            body: JSON.stringify(data),
        });

        if (!res.ok) {
            const text = await res.text();
            throw new Error(text || `Provisioning failed: ${res.status}`);
        }

        const reader = res.body?.getReader();
        if (!reader) throw new Error('No response stream');

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
                if (!line.trim()) continue;
                try {
                    onLog(JSON.parse(line));
                } catch { /* skip malformed */ }
            }
        }

        // Process remaining buffer
        if (buffer.trim()) {
            try {
                onLog(JSON.parse(buffer));
            } catch { /* skip */ }
        }
    }

    async getProvisioningInfo(assistantId: string, organizationId: string, subOrganizationId: string) {
        const response = await bexHttpClient.get(
            `/assistant/provisioning/info?assistant_id=${assistantId}&organization_id=${organizationId}&sub_organization_id=${subOrganizationId}`
        );
        return response;
    }

    async sendConfigEmail(assistantId: string, organizationId: string, subOrganizationId: string, email: string, assistantTitle: string) {
        const response = await bexHttpClient.post('/assistant/provisioning/send-config-email', {
            assistant_id: assistantId,
            assistant_title: assistantTitle,
            organization_id: organizationId,
            sub_organization_id: subOrganizationId,
            email,
        });
        return response;
    }

    async rotateAuthKey(assistantId: string, organizationId: string, subOrganizationId: string) {
        const response = await bexHttpClient.put('/assistant/provisioning/rotate-key', {
            assistant_id: assistantId,
            organization_id: organizationId,
            sub_organization_id: subOrganizationId,
        });
        return response;
    }

}

const assistantProvisioningService = new AssistantProvisioningService();
export { assistantProvisioningService };
